import { styled } from '@mui/material/styles';
import { keyframes } from '@emotion/react';
import { Box, Typography } from '@mui/material';
import { CAV6 } from '../cav6tokens';

/* ─── Ticket palette & geometry (sampled from the mockup at 1×) ───
   The mockup's coral is the account's brand color: primary actions use
   theme.palette.primary (main + contrastText), never a fixed hex. */
export const TICKET = {
  pageBg: '#f9f8f5',
  paper: '#f6f4f0',
  labelInk: '#17232f',
  tagline: '#6b6c6a',
  statText: '#7a7a80',
  termsText: '#7c7f84',
  link: '#2f63c2',
  dash: '#d0cec9',
  recordInk: '#1d2e38',
  recordBorder: '#8f8f91',
  iconGrey: '#919397',
  squiggle: '#c8c4bf',
  maxWidth: '622px',
  notch: 20, // px — radius of the side cut-outs at each perforation
  bottomNotch: 16, // px — radius of the cut-out at the bottom centre
  corner: 14, // px — radius of the quarter cuts at the bottom corners
  scallop: 8, // px — radius of the scalloped edge bites
  scallopPitch: 30, // px — distance between bites
  scallopOffset: 16, // px — first bite offset from the top
} as const;

/* ─── Proportional scale ───
   The mockup is a 622px-wide ticket. Geometry scales linearly with the real
   ticket width (`--u` = 1/622 of it, floored so small phones don't crush tap
   targets); type scales more gently (`--t`) so it stays legible on phones.
   All numbers below are the mockup's 1× pixel values. */
export const g = (n: number) => `calc(${n} * var(--u))`;

/* Only the tagline and label are Poppins in the mockup; buttons, stats and
   terms are set in the platform's system sans. */
export const BODY_FONT =
  'ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';
export const t = (n: number) => `calc(${n} * var(--t))`;

/* ─── Shell ─── */

export const HomeShell = styled(Box)({
  // Ticket width = min(viewport − 2×14px gutter, Layout's 540px cap − 28px).
  '--u': `max(0.62px, calc(min(100vw - 28px, 512px) / 622))`,
  '--t': `calc(0.45px + 0.55 * var(--u))`,
  minHeight: '100%',
  width: '100%',
  padding: '0 14px',
  paddingTop: `calc(${g(54)} + env(safe-area-inset-top, 0px))`,
  paddingBottom: `calc(${g(40)} + env(safe-area-inset-bottom, 0px))`,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  background: TICKET.pageBg,
  fontFamily: CAV6.font,
  color: TICKET.labelInk,
  WebkitFontSmoothing: 'antialiased',
});

export const Column = styled(Box)({
  width: '100%',
  maxWidth: TICKET.maxWidth,
  display: 'flex',
  flexDirection: 'column',
});

/* ─── Loading skeleton ───
   Shimmer block used for every asset that arrives asynchronously: the
   account logo, the ticket artwork and the measured upload speed. */

const shimmer = keyframes`
  from { background-position: 200% 0; }
  to { background-position: -200% 0; }
`;

export const Skeleton = styled('span')({
  display: 'block',
  borderRadius: g(8),
  background: 'linear-gradient(90deg, rgba(0,0,0,.055) 25%, rgba(0,0,0,.025) 50%, rgba(0,0,0,.055) 75%)',
  backgroundSize: '200% 100%',
  animation: `${shimmer} 1.6s ease-in-out infinite`,
  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
});

/* ─── Header: logo + tagline + label ─── */

/** Smallest rendered logo height on any screen. */
const LOGO_MIN_PX = 40;

export const LogoRow = styled(Box)({
  position: 'relative',
  width: '100%',
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
  minHeight: `max(${LOGO_MIN_PX}px, ${g(46)})`,
});

export const LogoSkeleton = styled(Skeleton)({
  width: `max(${LOGO_MIN_PX * 3.5}px, ${g(170)})`,
  height: `max(${LOGO_MIN_PX - 4}px, ${g(40)})`,
});

export const LogoImage = styled('img', { shouldForwardProp: (prop) => prop !== '$ready' })<{ $ready: boolean }>(
  ({ $ready }) => ({
    // Kept in the DOM while loading so onLoad fires, but not visible or taking
    // space until then — the skeleton owns the row in the meantime.
    position: $ready ? 'static' : 'absolute',
    opacity: $ready ? 1 : 0,
    // Mockup proportion, but never below LOGO_MIN_PX: scaled to a phone the
    // mockup's 46px became ~29px and logos read as thumbnails.
    height: `max(${LOGO_MIN_PX}px, ${g(46)})`,
    maxWidth: '55%',
    width: 'auto',
    objectFit: 'contain',
    objectPosition: 'left center',
    display: 'block',
  }),
);

export const LogoFallback = styled(Typography)({
  fontSize: t(34),
  fontWeight: 700,
  fontFamily: CAV6.font,
  letterSpacing: '-0.02em',
  color: TICKET.labelInk,
  lineHeight: 1.1,
});

export const Tagline = styled(Typography)({
  marginTop: g(18),
  fontFamily: CAV6.font,
  fontStyle: 'italic',
  fontWeight: 500,
  fontSize: t(19),
  lineHeight: t(28),
  color: TICKET.tagline,
});

export const TicketLabel = styled(Typography)({
  alignSelf: 'center',
  margin: `${g(40)} 0`,
  padding: `${g(5)} ${g(14)}`,
  fontFamily: CAV6.font,
  fontWeight: 600,
  fontSize: t(20),
  lineHeight: t(21),
  letterSpacing: '0.3em',
  textIndent: '0.3em', // balances the trailing letter-spacing so the text sits centred
  textTransform: 'uppercase',
  color: TICKET.labelInk,
  whiteSpace: 'nowrap',
});

/* ─── Ticket card ───
   The paper's real silhouette is cut with a CSS mask: a solid base layer
   minus the scallop bites along both long edges, the semicircle notches at
   each perforation (their y positions are measured at runtime and passed in
   as --n1/--n2), the bottom-centre notch and the bottom-corner cuts. The
   shadow lives on a wrapper as a drop-shadow filter so it follows the cut
   outline instead of the rectangle. */

const R = TICKET.scallop;
const hole = (at: string, r: number) =>
  `radial-gradient(circle at ${at}, #000 ${g(r)}, transparent calc(${g(r)} + 0.5px))`;

const MASK_LAYERS = [
  'linear-gradient(#000, #000)', // base (composited as base − holes)
  hole('0 50%', R), // left bites (tiled)
  hole('100% 50%', R), // right bites (tiled)
  hole('0 var(--n1)', TICKET.notch),
  hole('100% var(--n1)', TICKET.notch),
  hole('0 var(--n2)', TICKET.notch),
  hole('100% var(--n2)', TICKET.notch),
  hole('50% 100%', TICKET.bottomNotch),
  hole('0 100%', TICKET.corner),
  hole('100% 100%', TICKET.corner),
].join(', ');
const TILE = `${g(R * 2)} ${g(TICKET.scallopPitch)}`;
const MASK_SIZE = ['100% 100%', TILE, TILE, ...Array(7).fill('100% 100%')].join(', ');
const MASK_REPEAT = ['no-repeat', 'repeat-y', 'repeat-y', ...Array(7).fill('no-repeat')].join(', ');
const MASK_POSITION = [
  '0 0',
  `0 ${g(TICKET.scallopOffset)}`,
  `100% ${g(TICKET.scallopOffset)}`,
  ...Array(7).fill('0 0'),
].join(', ');

export const TicketOuter = styled(Box)({
  position: 'relative',
  width: '100%',
  filter:
    'drop-shadow(0 0 0.7px rgba(0,0,0,.28)) drop-shadow(0 9px 10px rgba(0,0,0,.13)) drop-shadow(3px 2px 5px rgba(0,0,0,.05))',
});

export const TicketCard = styled(Box)({
  '--n1': '-999px',
  '--n2': '-999px',
  position: 'relative',
  width: '100%',
  background: TICKET.paper,
  borderRadius: g(6),
  display: 'flex',
  flexDirection: 'column',
  WebkitMaskImage: MASK_LAYERS,
  WebkitMaskSize: MASK_SIZE,
  WebkitMaskRepeat: MASK_REPEAT,
  WebkitMaskPosition: MASK_POSITION,
  WebkitMaskComposite:
    'source-out, source-over, source-over, source-over, source-over, source-over, source-over, source-over, source-over, source-over',
  maskImage: MASK_LAYERS,
  maskSize: MASK_SIZE,
  maskRepeat: MASK_REPEAT,
  maskPosition: MASK_POSITION,
  maskComposite: 'subtract, add, add, add, add, add, add, add, add, add',
});

/* Dashed rule: 8px dash / 13px gap, 2px thick. The notches beside it are cut
   from the card's mask (see TicketCard). */
export const Perforation = styled(Box)({
  position: 'relative',
  height: '2px',
  margin: `0 ${g(58)}`,
  backgroundImage: `repeating-linear-gradient(90deg, ${TICKET.dash} 0 ${g(8)}, transparent ${g(8)} ${g(21)})`,
});

/* ─── Ticket sections ─── */

export const HeroSection = styled(Box)({
  padding: `${g(40)} ${g(40)} ${g(27)}`,
});

export const HeroFrame = styled('button')(({ theme }) => ({
  // The artwork is a second way in: tapping it opens the file picker, same
  // as Choose Video. Styled as a plain frame so it still reads as artwork.
  appearance: 'none',
  display: 'block',
  padding: 0,
  border: 0,
  cursor: 'pointer',
  WebkitTapHighlightColor: 'transparent',
  position: 'relative',
  width: '100%',
  aspectRatio: '16 / 9',
  borderRadius: g(16),
  overflow: 'hidden',
  background: 'linear-gradient(135deg, #d9d6cf 0%, #c4c0b7 100%)',
  '&:focus-visible': { outline: `3px solid ${theme.palette.primary.main}`, outlineOffset: '3px' },
  '&:disabled': { cursor: 'default' },
}));

export const HeroImage = styled('img', { shouldForwardProp: (prop) => prop !== '$ready' })<{ $ready: boolean }>(
  ({ $ready }) => ({
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    // Poster frames are usually portrait; bias the crop upward so faces survive.
    objectPosition: 'center 30%',
    display: 'block',
    opacity: $ready ? 1 : 0,
    transition: 'opacity 240ms ease',
  }),
);

export const HeroSkeleton = styled(Skeleton)({
  position: 'absolute',
  inset: 0,
  borderRadius: 0,
});

export const HeroBadge = styled(Box)({
  position: 'absolute',
  left: '50%',
  top: '50%',
  transform: 'translate(-50%, -50%)',
  width: g(87),
  height: g(87),
  borderRadius: '50%',
  background: 'rgba(255,255,255,.38)',
  display: 'grid',
  placeItems: 'center',
  color: '#fff',
  '& svg': { width: g(42), height: g(42) },
});

export const ActionsSection = styled(Box)({
  padding: `${g(28)} ${g(55)} ${g(66)}`,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: g(20),
});

const buttonBase = {
  appearance: 'none' as const,
  fontFamily: BODY_FONT,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  WebkitTapHighlightColor: 'transparent',
  transition: 'transform 160ms ease, opacity 160ms ease',
  '&:active': { transform: 'scale(0.985)' },
};

export const ChooseButton = styled('button')(({ theme }) => ({
  ...buttonBase,
  gap: g(34),
  width: '100%',
  height: g(114),
  border: 0,
  borderRadius: g(16),
  background: theme.palette.primary.main,
  boxShadow: '0 1.5px 2px rgba(0,0,0,.32)',
  color: theme.palette.primary.contrastText,
  fontSize: t(27),
  fontWeight: 500,
  lineHeight: 1,
  letterSpacing: 0,
  whiteSpace: 'nowrap',
  '& svg': { width: g(36), height: g(32) },
  '& svg .knockout': { fill: theme.palette.primary.main },
}));

export const RecordButton = styled('button')({
  ...buttonBase,
  gap: g(14),
  width: g(304),
  maxWidth: '100%',
  height: g(52),
  minHeight: '40px', // keep a usable tap target on small phones
  background: 'rgba(255,255,255,.55)',
  border: `1px solid ${TICKET.recordBorder}`,
  borderRadius: g(8),
  color: TICKET.recordInk,
  fontSize: t(16),
  fontWeight: 600,
  lineHeight: 1,
  whiteSpace: 'nowrap',
  '& svg': { width: g(27), height: g(20) },
});

export const ExampleLink = styled('button')({
  ...buttonBase,
  gap: '6px',
  marginTop: g(6),
  padding: '4px 8px',
  background: 'transparent',
  border: 0,
  fontSize: t(14),
  fontWeight: 500,
  color: TICKET.statText,
  textDecoration: 'underline',
  textUnderlineOffset: '3px',
  '& svg': { width: '18px', height: '18px' },
});

export const FooterSection = styled(Box)({
  position: 'relative',
  padding: `${g(38)} ${g(50)} ${g(44)}`,
  display: 'flex',
  flexDirection: 'column',
  gap: g(20),
});

/* Single line, as in the mockup: limits on the left, the speed reading on
   the right, the squiggle just past it. This row keeps the mockup's own
   proportions (`--s`, the unfloored scale) so it always fits on one line;
   type is floored at 9px. */
export const StatsRow = styled(Box)({
  '--s': 'calc(min(100vw - 28px, 512px) / 622)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 'calc(14 * var(--s))',
  flexWrap: 'nowrap',
  // Below 360px even the floored type cannot fit both stats on one line
  // without running under the squiggle; let the speed reading drop down.
  '@media (max-width: 359px)': { flexWrap: 'wrap', rowGap: 'calc(10 * var(--s))' },
  // Mockup: the speed reading ends ~65px (of 622) from the paper's right
  // edge, an 8px gap before the squiggle. Footer padding is 50, so +15.
  marginRight: 'calc(15 * var(--s))',
});

export const Stat = styled(Box)({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 'calc(12 * var(--s))',
  fontFamily: BODY_FONT,
  fontSize: 'max(9px, calc(14.5 * var(--s)))',
  fontWeight: 400,
  lineHeight: 1,
  color: TICKET.statText,
  whiteSpace: 'nowrap',
  flex: '0 0 auto',
  '& svg': { flex: '0 0 auto', width: 'auto', height: 'calc(33 * var(--s))' },
});

export const SpeedSkeleton = styled(Skeleton)({
  display: 'inline-block',
  verticalAlign: 'middle',
  width: '4em',
  height: '0.9em',
  borderRadius: '3px',
});

export const StatDot = styled('span')({
  margin: '0 0.3em',
});

export const TermsLine = styled(Typography)({
  fontFamily: BODY_FONT,
  fontSize: t(15),
  fontWeight: 400,
  color: '#7c7f84',
  lineHeight: t(22),
  '& a': {
    color: TICKET.link,
    fontWeight: 500,
    textDecoration: 'underline',
    textDecorationThickness: '1.5px',
    textUnderlineOffset: '2px',
  },
});

export const Squiggle = styled('svg')({
  position: 'absolute',
  right: g(-2),
  top: g(28),
  width: g(44),
  height: g(70),
  color: TICKET.squiggle,
  pointerEvents: 'none',
});
